import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';

import GenericForm from '../GenericForm';
import { ProformaInvoiceDraft } from 'src/types/proformaInvoice';
import { cloneSettingsAsNewForInvoice, createEmptyDraft } from '../mappers';
import { useAppDispatch, useAppStore } from 'src/app/hooks';
import { selectCurrentUserMemberData } from 'src/slices/workspacesSlice';
import { selectProposalSettings } from 'src/slices/proposalsSlice';
import { createProformaInvoice as createProformaInvoiceThunk } from 'src/slices/proformaInvoicesSlice/proformaInvoicesSlice';
import { MessageType } from 'components/UI/snackbar/providers/SnackBarProvider';
import { ProformaInvoiceFormValues } from '../types';
import { PROFORMA_INVOICE_STATUS } from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';

interface Props {
  defaultValues?: Partial<ProformaInvoiceDraft>;
  onSuccess?: () => void;
  onCancel: () => void;
  onFormDataChanged: () => void;
}

export default function CreateForm({
  defaultValues,
  onSuccess,
  onCancel,
  onFormDataChanged,
}: Props) {
  const { t } = useTranslation('proforma-invoices-page');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const state = store.getState();
  const settingsFromStore = selectProposalSettings(state);
  const currentUser = selectCurrentUserMemberData(state);
  const initialDraft: ProformaInvoiceDraft = useMemo(() => {
    const base = createEmptyDraft();

    const draft: ProformaInvoiceDraft = {
      ...base,
      ...defaultValues,
      // The client assigns the uuid, as for other entities; the backend requires it.
      // A retried save with the same uuid updates the invoice instead of duplicating it.
      uuid: uuidv4(),
      invoiceId: defaultValues?.invoiceId ?? null,
      priceProposalId: defaultValues?.priceProposalId ?? null,
      priceProposalReference: defaultValues?.priceProposalReference ?? null,
      contactId: defaultValues?.contactId ?? null,
      contactName: defaultValues?.contactName ?? null,
      title: defaultValues?.title ?? '',
      description: defaultValues?.description ?? '',
      discountType: defaultValues?.discountType ?? base.discountType,
      discountAmount: defaultValues?.discountAmount ?? base.discountAmount,
      items:
        defaultValues?.items && defaultValues.items.length > 0 ? defaultValues.items : base.items,
      // Starts from the workspace quote settings, as a new settings object of this invoice.
      settings: cloneSettingsAsNewForInvoice(settingsFromStore),
      subtotalPrice: defaultValues?.subtotalPrice ?? null,
      priceWithDiscount: defaultValues?.priceWithDiscount ?? null,
      taxValue: defaultValues?.taxValue ?? null,
      priceWithTax: defaultValues?.priceWithTax ?? null,
      dueDate: defaultValues?.dueDate ?? null,
      createdAt: null,
      updatedAt: null,
      createdBy: currentUser?.email ?? null,
      updatedBy: currentUser?.email ?? null,
      priceProposalOverrideFields: defaultValues?.priceProposalOverrideFields ?? false,
      priceProposalOverrideSettings: defaultValues?.priceProposalOverrideSettings ?? false,
      status: defaultValues?.status ?? PROFORMA_INVOICE_STATUS.PENDING_PAYMENT,
    };

    return draft;
  }, [defaultValues, settingsFromStore, currentUser]);

  const handleSubmit = useCallback(
    async (values: ProformaInvoiceFormValues) => {
      const draft: ProformaInvoiceDraft = {
        ...values,
        createdBy: currentUser?.email ?? null,
        updatedBy: currentUser?.email ?? null,
      };

      try {
        await dispatch(createProformaInvoiceThunk(draft)).unwrap();

        enqueueSnackbar({
          message: t('form.notification.created'),
          variant: MessageType.Success,
        });

        onSuccess?.();
      } catch (error) {
        enqueueSnackbar({
          message: t('common:message.server_error'),
          variant: MessageType.Error,
        });
      }
    },
    [dispatch, enqueueSnackbar, onSuccess, t, currentUser]
  );

  return (
    <GenericForm
      defaultValues={initialDraft}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      onFormDataChanged={onFormDataChanged}
      mode="create"
    />
  );
}
